// DERIVADO DE: gestion-web-cliente
//   src/app/auxiliares/servicios/helper.service.ts → `sonidoMuteado`, `toggleSonido`
//   src/app/auxiliares/servicios/listado-eventos-genericos-vivo.ts
//     → `sonidos`, `InitBackSound`, `stopBackSound`
//   src/app/auxiliares/servicios/idle.service.ts
// CAMBIOS: `HTMLAudioElement` en vez de Howler (no se suma una dependencia para
//   tres archivos cortos); la preferencia es una signal; la inactividad se
//   detecta acá mismo, sin un servicio aparte.

import { DOCUMENT } from '@angular/common';
import { Service, inject, signal } from '@angular/core';

/**
 * Los sonidos que se pueden elegir para una categoría de evento. Son los mismos
 * valores que guarda IRIX (`SonidoEvento`), y `Silencio` es no sonar.
 */
export type Sonido = 'Campana' | 'Sirena' | 'Silencio';

/** Los mismos archivos que IRIX (`assets/sonidos/`). */
const ARCHIVOS: Record<Exclude<Sonido, 'Silencio'>, string> = {
  Campana: 'assets/sonidos/campana.mp3',
  Sirena: 'assets/sonidos/sirena1.mp3',
};
const ARCHIVO_FONDO = 'assets/sonidos/empty_loop_for_js_performance.ogg';

/** Como IRIX (`IdleService.idleAfterSeconds`): diez segundos sin tocar nada. */
const INACTIVIDAD_SEG = 10;
/** Lo que cuenta como interacción, igual que en IRIX. */
const EVENTOS_INTERACCION = ['mousemove', 'touchstart', 'keydown'] as const;

const CLAVE_SILENCIADO = 'faro.sonidos-silenciados';

function leerSilenciado(): boolean {
  try {
    return localStorage.getItem(CLAVE_SILENCIADO) === 'true';
  } catch {
    return false;
  }
}

/**
 * Los avisos sonoros de la aplicación y la preferencia de silenciarlos.
 *
 * Como en IRIX, **la preferencia es del navegador**, no del usuario: vive en
 * `localStorage` y arranca con sonido. El que silencia en la PC del centro de
 * control no silencia la del supervisor.
 *
 * El navegador no deja reproducir audio en una página con la que todavía nadie
 * interactuó (política de autoplay). Después de un F5 sin tocar nada, el primer
 * aviso puede no sonar; no es un error y no se reintenta.
 */
@Service()
export class SonidosService {
  private readonly documento = inject(DOCUMENT);
  private readonly _silenciado = signal(leerSilenciado());
  private readonly audios = new Map<string, HTMLAudioElement>();
  private fondoIniciado = false;

  readonly silenciado = this._silenciado.asReadonly();

  setSilenciado(silenciado: boolean): void {
    this._silenciado.set(silenciado);
    try {
      localStorage.setItem(CLAVE_SILENCIADO, String(silenciado));
    } catch {
      /* almacenamiento bloqueado: la preferencia dura lo que la pestaña */
    }
  }

  reproducir(sonido: Sonido): void {
    if (this._silenciado() || sonido === 'Silencio') return;
    const audio = this.audio(ARCHIVOS[sonido]);
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => undefined);
  }

  /**
   * El bucle mudo de IRIX: con el operador inactivo diez segundos, suena en loop
   * un archivo sin sonido, y se corta con la primera interacción.
   *
   * **Para qué.** El navegador espacia los temporizadores de una pestaña que no
   * se usa —hasta uno por minuto—, y el aviso de eventos nuevos es justamente un
   * temporizador. Una pestaña que está reproduciendo audio queda exenta. Como en
   * IRIX, silenciado no arranca (y si se silencia con el bucle andando, sigue
   * hasta la próxima interacción: no se oye igual).
   *
   * Se llama una vez; las siguientes no hacen nada.
   */
  iniciarFondoEnInactividad(): void {
    if (this.fondoIniciado) return;
    this.fondoIniciado = true;

    let inactivo = false;
    let temporizador: ReturnType<typeof setTimeout> | undefined;
    const interaccion = () => {
      if (inactivo) {
        inactivo = false;
        this.detenerFondo();
      }
      clearTimeout(temporizador);
      temporizador = setTimeout(() => {
        inactivo = true;
        this.sonarFondo();
      }, INACTIVIDAD_SEG * 1000);
    };
    for (const evento of EVENTOS_INTERACCION) {
      this.documento.addEventListener(evento, interaccion, { passive: true });
    }
    interaccion();
  }

  private sonarFondo(): void {
    if (this._silenciado()) return;
    const audio = this.audio(ARCHIVO_FONDO, { loop: true, volumen: 0.5 });
    audio?.play().catch(() => undefined);
  }

  private detenerFondo(): void {
    const audio = this.audios.get(ARCHIVO_FONDO);
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }

  /** Uno por archivo, creado recién la primera vez que suena. */
  private audio(
    src: string,
    opciones: { loop?: boolean; volumen?: number } = {},
  ): HTMLAudioElement | undefined {
    const Audio = this.documento.defaultView?.Audio;
    if (!Audio) return undefined;
    let audio = this.audios.get(src);
    if (!audio) {
      audio = new Audio(src);
      audio.loop = opciones.loop ?? false;
      audio.volume = opciones.volumen ?? 1;
      this.audios.set(src, audio);
    }
    return audio;
  }
}
